import { useContext } from 'react';
import type { FieldSchema } from '../types';
import ImageUploader from './ImageUploader';
import ContentEditorWithPreview from './ContentEditorWithPreview';
import { GlobalPreviewContext } from '../hooks/useGlobalPreview';

interface FormFieldProps {
  name: string;
  schema: FieldSchema;
  value: unknown;
  onChange: (name: string, value: unknown) => void;
  siteId: string;
  /** When provided, hovering this field highlights the matching element in the preview */
  cmsType?: string;
  cmsId?: string;
}

function FieldHint({ text, charCount, maxLength }: { text?: string; charCount?: number; maxLength?: number }) {
  if (!text && !maxLength) return null;
  return (
    <span className="field-hint">
      {text}
      {maxLength && (
        <span style={{ float: 'right' }}>{charCount ?? 0}/{maxLength}</span>
      )}
    </span>
  );
}

export default function FormField({
  name,
  schema,
  value,
  onChange,
  siteId,
  cmsType,
  cmsId,
}: FormFieldProps) {
  const preview = useContext(GlobalPreviewContext);
  const hoverProps = cmsType && preview ? {
    onMouseEnter: () => preview.highlightField(cmsType, name, cmsId),
    onMouseLeave: () => preview.highlightField(null),
  } : {};

  switch (schema.type) {
    case 'text':
      return (
        <div className="form-field" {...hoverProps}>
          <label className="field-label">
            {schema.label}
            {schema.required && <span className="required">*</span>}
          </label>
          <input
            type="text"
            className="field-input"
            value={(value as string) || ''}
            onChange={(e) => onChange(name, e.target.value)}
            placeholder={schema.placeholder}
            maxLength={schema.maxLength}
            required={schema.required}
          />
          <FieldHint
            text={schema.hint}
            charCount={((value as string) || '').length}
            maxLength={schema.maxLength}
          />
        </div>
      );

    case 'richtext':
      return (
        <div className="form-field" {...hoverProps}>
          <ContentEditorWithPreview
            label={schema.label}
            required={schema.required}
            value={(value as string) || ''}
            onChange={(html) => onChange(name, html)}
            placeholder={`Enter ${schema.label.toLowerCase()}…`}
          />
          {schema.hint && <span className="field-hint">{schema.hint}</span>}
        </div>
      );

    case 'textarea':
      return (
        <div className="form-field" {...hoverProps}>
          <label className="field-label">
            {schema.label}
            {schema.required && <span className="required">*</span>}
          </label>
          <textarea
            className="field-input field-textarea"
            value={(value as string) || ''}
            onChange={(e) => onChange(name, e.target.value)}
            placeholder={schema.placeholder}
            rows={4}
            required={schema.required}
          />
          {schema.hint && <span className="field-hint">{schema.hint}</span>}
        </div>
      );

    case 'image':
      return (
        <div className="form-field" {...hoverProps}>
          <ImageUploader
            siteId={siteId}
            label={`${schema.label}${schema.required ? ' *' : ''}`}
            value={(value as string) || ''}
            onChange={(key) => onChange(name, key)}
          />
          {schema.hint && <span className="field-hint">{schema.hint}</span>}
        </div>
      );

    case 'images': {
      const images = Array.isArray(value) ? (value as string[]) : [];
      return (
        <div className="form-field" {...hoverProps}>
          <label className="field-label">
            {schema.label}
            {schema.required && <span className="required">*</span>}
            {schema.max && (
              <span className="field-hint"> (max {schema.max})</span>
            )}
          </label>
          {schema.hint && <span className="field-hint" style={{ marginBottom: '8px', display: 'block' }}>{schema.hint}</span>}
          <div className="images-grid">
            {images.map((img, idx) => (
              <div key={idx} className="images-grid-item">
                <ImageUploader
                  siteId={siteId}
                  value={img}
                  onChange={(key) => {
                    const updated = [...images];
                    if (key) {
                      updated[idx] = key;
                    } else {
                      updated.splice(idx, 1);
                    }
                    onChange(name, updated);
                  }}
                />
              </div>
            ))}
            {(!schema.max || images.length < schema.max) && (
              <div className="images-grid-item">
                <ImageUploader
                  siteId={siteId}
                  value=""
                  onChange={(key) => {
                    if (key) onChange(name, [...images, key]);
                  }}
                />
              </div>
            )}
          </div>
        </div>
      );
    }

    case 'select':
      return (
        <div className="form-field" {...hoverProps}>
          <label className="field-label">
            {schema.label}
            {schema.required && <span className="required">*</span>}
          </label>
          <select
            className="field-input field-select"
            value={(value as string) || ''}
            onChange={(e) => onChange(name, e.target.value)}
            required={schema.required}
          >
            {schema.options?.map((opt) => (
              <option key={opt.value} value={opt.value}>
                {opt.label}
              </option>
            ))}
          </select>
          {schema.hint && <span className="field-hint">{schema.hint}</span>}
        </div>
      );

    case 'boolean':
      return (
        <div className="form-field form-field-toggle" {...hoverProps}>
          <label className="toggle-label">
            <div className="toggle-wrapper">
              <input
                type="checkbox"
                checked={Boolean(value)}
                onChange={(e) => onChange(name, e.target.checked)}
                className="toggle-input"
              />
              <div className="toggle-track">
                <div className="toggle-thumb" />
              </div>
            </div>
            <span>{schema.label}</span>
          </label>
          {schema.hint && <span className="field-hint" style={{ marginLeft: '56px' }}>{schema.hint}</span>}
        </div>
      );

    default:
      return null;
  }
}
